import config from 'src/config';
import engineApiSlice from 'src/shared/state/api-slices/engineApiSlice';
import { executeEngineQuery } from 'src/app/portfolio/state/api/engineQueryUtils';
import {
    riskPortfolioImportPreloadQuery,
    type ImportPreloadSite,
    type RiskPortfolioImportPreloadResult,
} from './queries/importPreloadQuery';
import type { ExistingDraftSite } from '../../hooks/useImportSites.types';

const engineGraphqlEndpoint = `${config.engineApiUrl.replace(/\/$/, '')}/query`;

export type RiskPortfolioImportPreloadArgs = {
    businessCentralId: string;
    page: number;
};

/**
 * Narrows one page of the GraphQL response to the shape `useImportSites`
 * consumes. Rows without a `businessCentralId` are dropped — the import
 * flow keys matches by that field, so an entry without one is unmatchable
 * and would be a pointless map insertion.
 */
export const toExistingDraftSites = (result: RiskPortfolioImportPreloadResult): ExistingDraftSite[] => {
    const items: ImportPreloadSite[] = result.organisation?.sites?.items ?? [];
    return items.flatMap(item =>
        item.businessCentralId === null
            ? []
            : [{ businessCentralId: item.businessCentralId, siteId: item.id, hasGeography: item.geography !== null }]
    );
};

/**
 * Pages through every draft site for the given organisation, accumulating
 * results until `pagination.hasNext` is false. The engine caps `pageSize`
 * at 100, so orgs with more than 100 draft sites need this loop to see
 * every recovery candidate. The transport is injected so the loop can be
 * unit-tested without RTK Query.
 */
export const fetchAllDraftSitesForImport = async (
    runPreload: (args: RiskPortfolioImportPreloadArgs) => Promise<RiskPortfolioImportPreloadResult>,
    businessCentralId: string
): Promise<ExistingDraftSite[]> => {
    const accumulated: ExistingDraftSite[] = [];
    let page = 1;
    while (true) {
        const result = await runPreload({ businessCentralId, page });
        accumulated.push(...toExistingDraftSites(result));
        if (!result.organisation?.sites?.pagination.hasNext) return accumulated;
        page += 1;
    }
};

const riskPortfolioEngineGraphSlice = engineApiSlice.injectEndpoints({
    endpoints: builder => ({
        getRiskPortfolioImportPreload: builder.query<RiskPortfolioImportPreloadResult, RiskPortfolioImportPreloadArgs>({
            queryFn: arg =>
                executeEngineQuery<RiskPortfolioImportPreloadResult>(engineGraphqlEndpoint, riskPortfolioImportPreloadQuery, {
                    businessCentralId: arg.businessCentralId,
                    page: arg.page,
                }),
        }),
    }),
});

export const { useLazyGetRiskPortfolioImportPreloadQuery } = riskPortfolioEngineGraphSlice;
export default riskPortfolioEngineGraphSlice;
